import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import am5locales_fr_FR from '@amcharts/amcharts5/locales/fr_FR';

type SharedTimeUnit =
  | 'millisecond'
  | 'second'
  | 'minute'
  | 'hour'
  | 'day'
  | 'week'
  | 'month'
  | 'year';

export type SharedTimeInterval = {
  timeUnit: SharedTimeUnit;
  count: number;
};

type XYChartOptions = {
  paddingTop?: number;
  paddingBottom?: number;
  paddingLeft?: number;
  paddingRight?: number;
  enableHorizontalZoom?: boolean;
  enableHorizontalPan?: boolean;
  showAllSeriesTooltips?: boolean;
};

type DateAxisOptions = {
  baseInterval?: SharedTimeInterval;
  minGridDistance?: number;
};

type CategoryAxisOptions = {
  categoryField?: string;
  minGridDistance?: number;
};

type ValueAxisOptions = {
  opposite?: boolean;
  min?: number;
  strictMinMax?: boolean;
  extraMax?: number;
  minGridDistance?: number;
  /** Unité affichée après chaque valeur des labels de l'axe (ex. `'MWh'`). */
  unite?: string;
};

type LegendOptions = {
  centerXPercent?: number;
  xPercent?: number;
  marginTop?: number;
};

type CursorOptions = {
  snapToSeries?: am5xy.XYSeries[];
  snapToSeriesBy?: 'xy' | 'x' | 'y' | 'x!' | 'y!';
  showVerticalLine?: boolean;
  showHorizontalLine?: boolean;
  verticalLineColor?: number;
  horizontalLineColor?: number;
  behavior?: 'none' | 'zoomX' | 'zoomY' | 'zoomXY' | 'selectX' | 'selectY' | 'selectXY';
  onRangeSelect?: (debut: Date, fin: Date) => void;
};

type LineSeriesOptions<T extends object> = {
  name: string;
  valueYField: keyof T & string;
  valueXField?: keyof T & string;
  color: number;
  yAxis: am5xy.ValueAxis<am5xy.AxisRendererY>;
  xAxis: am5xy.DateAxis<am5xy.AxisRendererX>;
  fillOpacity?: number;
  tooltipText?: string;
  showTooltip?: boolean;
  data: T[];
};

type ColumnSeriesOptions<T extends object> = {
  name: string;
  categoryXField: keyof T & string;
  valueYField: keyof T & string;
  color: number;
  xAxis: am5xy.CategoryAxis<am5xy.AxisRendererX>;
  yAxis: am5xy.ValueAxis<am5xy.AxisRendererY>;
  tooltipText?: string;
  showTooltip?: boolean;
  data: T[];
  widthPercent?: number;
};

export type SharedLineSeriesDefinition<T extends object> = Omit<
  LineSeriesOptions<T>,
  'xAxis' | 'data'
>;

export type SharedColumnSeriesDefinition<T extends object> = Omit<
  ColumnSeriesOptions<T>,
  'xAxis' | 'data'
>;

export function createChartRoot(host: HTMLDivElement): am5.Root {
  const root = am5.Root.new(host);
  root.locale = am5locales_fr_FR;
  root.setThemes([am5themes_Animated.new(root)]);
  root.interfaceColors.set('grid', am5.color(0xd7dce3));
  return root;
}

export function createXYChart(root: am5.Root, options: XYChartOptions = {}): am5xy.XYChart {
  return root.container.children.push(
    am5xy.XYChart.new(root, {
      panX: options.enableHorizontalPan ?? options.enableHorizontalZoom ?? false,
      panY: false,
      wheelX: options.enableHorizontalZoom ? 'zoomX' : 'none',
      wheelY: 'none',
      pinchZoomX: options.enableHorizontalZoom ?? false,
      layout: root.verticalLayout,
      paddingLeft: options.paddingLeft ?? 0,
      paddingRight: options.paddingRight ?? 0,
      paddingTop: options.paddingTop ?? 6,
      paddingBottom: options.paddingBottom ?? 0,
      arrangeTooltips: true,
      maxTooltipDistance: options.showAllSeriesTooltips ? -1 : 0,
    })
  );
}

export function createDateAxis(
  root: am5.Root,
  chart: am5xy.XYChart,
  options: DateAxisOptions = {}
): am5xy.DateAxis<am5xy.AxisRendererX> {
  const renderer = am5xy.AxisRendererX.new(root, {
    minGridDistance: options.minGridDistance ?? 70,
    strokeOpacity: 0.12,
  });

  renderer.grid.template.setAll({
    strokeDasharray: [4, 4],
    strokeOpacity: 0.7,
  });
  renderer.labels.template.setAll({
    fill: am5.color(0x4a4a4a),
    fontSize: 12,
    paddingTop: 8,
  });

  const axis: am5xy.DateAxis<am5xy.AxisRendererX> = chart.xAxes.push(
    am5xy.DateAxis.new(root, {
      baseInterval: options.baseInterval ?? { timeUnit: 'minute', count: 30 },
      maxDeviation: 0,
      renderer,
      tooltipDateFormat: 'dd/MM/yyyy HH:mm',
      tooltip: am5.Tooltip.new(root, {}),
    })
  );

  axis.get('tooltip')?.get('background')?.setAll({
    fill: am5.color(0xd71920),
    stroke: am5.color(0xd71920),
    fillOpacity: 1,
  });
  axis.get('tooltip')?.label.setAll({
    fill: am5.color(0xffffff),
    fontSize: 12,
    fontWeight: '600',
    paddingTop: 6,
    paddingBottom: 6,
    paddingLeft: 10,
    paddingRight: 10,
  });

  return axis;
}

export function createCategoryAxis<T extends object>(
  root: am5.Root,
  chart: am5xy.XYChart,
  data: T[],
  options: CategoryAxisOptions = {}
): am5xy.CategoryAxis<am5xy.AxisRendererX> {
  const renderer = am5xy.AxisRendererX.new(root, {
    minGridDistance: options.minGridDistance ?? 45,
    strokeOpacity: 0.12,
  });

  renderer.grid.template.setAll({
    strokeOpacity: 0.18,
  });
  renderer.labels.template.setAll({
    fill: am5.color(0x5d645f),
    fontSize: 12,
    paddingTop: 8,
  });

  const axis: am5xy.CategoryAxis<am5xy.AxisRendererX> = chart.xAxes.push(
    am5xy.CategoryAxis.new(root, {
      categoryField: options.categoryField ?? 'category',
      renderer,
    })
  );

  axis.data.setAll(data);

  return axis;
}

export function createValueAxis(
  root: am5.Root,
  chart: am5xy.XYChart,
  options: ValueAxisOptions = {}
): am5xy.ValueAxis<am5xy.AxisRendererY> {
  const renderer = am5xy.AxisRendererY.new(root, {
    opposite: options.opposite ?? false,
    minGridDistance: options.minGridDistance ?? 28,
    strokeOpacity: 0.12,
  });

  renderer.grid.template.setAll({
    strokeDasharray: [4, 4],
    strokeOpacity: 0.7,
  });
  renderer.labels.template.setAll({
    fill: am5.color(0x4a4a4a),
    fontSize: 12,
  });

  return chart.yAxes.push(
    am5xy.ValueAxis.new(root, {
      ...(options.min !== undefined ? { min: options.min } : {}),
      ...(options.strictMinMax !== undefined ? { strictMinMax: options.strictMinMax } : {}),
      extraMax: options.extraMax ?? 0.08,
      ...(options.unite ? { numberFormat: `#,###.##' ${options.unite}'` } : {}),
      renderer,
    })
  );
}

export function createLineSeries<T extends object>(
  root: am5.Root,
  chart: am5xy.XYChart,
  options: LineSeriesOptions<T>
): am5xy.LineSeries {
  const series = chart.series.push(
    am5xy.LineSeries.new(root, {
      name: options.name,
      xAxis: options.xAxis,
      yAxis: options.yAxis,
      valueYField: options.valueYField,
      valueXField: options.valueXField ?? 'date',
      stroke: am5.color(options.color),
      fill: am5.color(options.color),
      maskBullets: false,
      snapTooltip: true,
    })
  );

  series.strokes.template.setAll({
    strokeWidth: 2,
  });
  series.fills.template.setAll({
    visible: (options.fillOpacity ?? 0) > 0,
    fillOpacity: options.fillOpacity ?? 0,
  });
  if (options.showTooltip !== false) {
    series.setAll({
      tooltip: am5.Tooltip.new(root, {
        labelText: options.tooltipText ?? `{name}: {valueY}`,
      }),
    });
  }
  series.data.setAll(options.data);

  return series;
}

export function createColumnSeries<T extends object>(
  root: am5.Root,
  chart: am5xy.XYChart,
  options: ColumnSeriesOptions<T>
): am5xy.ColumnSeries {
  const series = chart.series.push(
    am5xy.ColumnSeries.new(root, {
      name: options.name,
      xAxis: options.xAxis,
      yAxis: options.yAxis,
      categoryXField: options.categoryXField,
      valueYField: options.valueYField,
      fill: am5.color(options.color),
      stroke: am5.color(options.color),
      clustered: true,
    })
  );

  series.columns.template.setAll({
    width: am5.percent(options.widthPercent ?? 70),
    cornerRadiusTL: 3,
    cornerRadiusTR: 3,
    strokeOpacity: 0,
    fillOpacity: 0.95,
  });
  if (options.showTooltip !== false) {
    series.setAll({
      tooltip: am5.Tooltip.new(root, {
        labelText: options.tooltipText ?? `{name}: {valueY}`,
      }),
    });
  }
  series.data.setAll(options.data);

  return series;
}

export function createLineSeriesCollection<T extends object>(
  root: am5.Root,
  chart: am5xy.XYChart,
  xAxis: am5xy.DateAxis<am5xy.AxisRendererX>,
  data: T[],
  seriesDefinitions: SharedLineSeriesDefinition<T>[]
): am5xy.LineSeries[] {
  return seriesDefinitions.map((definition) =>
    createLineSeries(root, chart, {
      ...definition,
      xAxis,
      data,
    })
  );
}

export function createColumnSeriesCollection<T extends object>(
  root: am5.Root,
  chart: am5xy.XYChart,
  xAxis: am5xy.CategoryAxis<am5xy.AxisRendererX>,
  data: T[],
  seriesDefinitions: SharedColumnSeriesDefinition<T>[]
): am5xy.ColumnSeries[] {
  return seriesDefinitions.map((definition) =>
    createColumnSeries(root, chart, {
      ...definition,
      xAxis,
      data,
    })
  );
}

export function createToggleLegend(
  root: am5.Root,
  chart: am5xy.XYChart,
  series: am5xy.XYSeries[],
  options: LegendOptions = {}
): am5.Legend {
  const legend = chart.children.push(
    am5.Legend.new(root, {
      centerX: am5.percent(options.centerXPercent ?? 50),
      x: am5.percent(options.xPercent ?? 50),
      marginTop: options.marginTop ?? 16,
      useDefaultMarker: true,
      clickTarget: 'none',
    })
  );

  legend.labels.template.setAll({
    fill: am5.color(0x2f2f2f),
    fontSize: 12,
  });

  legend.valueLabels.template.set('forceHidden', true);
  legend.itemContainers.template.setAll({
    cursorOverStyle: 'pointer',
  });

  legend.itemContainers.template.events.on('click', (event) => {
    const clickedSeries = event.target.dataItem?.dataContext as am5xy.XYSeries | undefined;

    if (!clickedSeries) {
      return;
    }

    if (clickedSeries.get('visible')) {
      clickedSeries.hide(0);
    } else {
      clickedSeries.show(0);
    }
  });

  legend.data.setAll(series);

  return legend;
}

export function createXYCursor(
  root: am5.Root,
  chart: am5xy.XYChart,
  options: CursorOptions = {}
): am5xy.XYCursor {
  const cursor = chart.set(
    'cursor',
    am5xy.XYCursor.new(root, {
      behavior: options.behavior ?? 'none',
      xAxis: chart.xAxes.getIndex(0),
      // Pour une série temporelle, le point pertinent dépend de l'abscisse et
      // non de la distance verticale entre la souris et la courbe.
      snapToSeriesBy: options.snapToSeriesBy ?? 'x',
    })
  );

  cursor.lineX.setAll({
    stroke: am5.color(options.verticalLineColor ?? 0xd71920),
    strokeOpacity: options.showVerticalLine === false ? 0 : 0.7,
    strokeWidth: 2,
  });

  cursor.lineY.setAll({
    stroke: am5.color(options.horizontalLineColor ?? 0x8d97a7),
    strokeOpacity: options.showHorizontalLine === false ? 0 : 0.35,
    strokeDasharray: [4, 4],
  });

  if (options.snapToSeries?.length) {
    cursor.set('snapToSeries', options.snapToSeries);

    const xAxis = chart.xAxes.getIndex(0) as am5xy.DateAxis<am5xy.AxisRendererX> | undefined;
    let frameRequestId: number | null = null;
    let pendingPositionX: number | undefined;

    const masquerTooltips = () => {
      options.snapToSeries?.forEach((series) => {
        series.hideTooltip();
      });
      xAxis?.get('tooltip')?.hide();
    };

    const synchroniserTooltips = (positionX: number) => {
      if (!xAxis) {
        return;
      }

      let tooltipAnchorPosition: number | undefined;

      options.snapToSeries?.forEach((series) => {
        if (!series.get('visible')) {
          series.hideTooltip();
          return;
        }

        // 0.5 correspond au centre de l'intervalle. Une location à 0 décale la
        // recherche d'un demi-baseInterval et peut accrocher le point suivant.
        const dataItem = xAxis.getSeriesItem(series, positionX, 0.5, true);
        series.showDataItemTooltip(dataItem);

        if (tooltipAnchorPosition == null && dataItem) {
          tooltipAnchorPosition = xAxis.getDataItemPositionX(dataItem, 'valueX', 0, 0, true);
        }
      });

      if (tooltipAnchorPosition != null) {
        xAxis.setPrivate('tooltipPosition', tooltipAnchorPosition);
        xAxis.updateTooltip();
      } else {
        xAxis.get('tooltip')?.hide();
      }
    };

    const planifierSynchronisation = (positionX: number) => {
      pendingPositionX = positionX;

      if (frameRequestId != null) {
        return;
      }

      frameRequestId = requestAnimationFrame(() => {
        frameRequestId = null;

        if (pendingPositionX == null) {
          return;
        }

        synchroniserTooltips(pendingPositionX);
      });
    };

    cursor.events.on('selectstarted', () => {
      const positionX = cursor.getPrivate('positionX');
      if (positionX != null) {
        planifierSynchronisation(positionX);
      }
    });

    cursor.events.on('selectended', () => {
      if (options.behavior !== 'selectX' || !xAxis) {
        return;
      }

      const startPosition = cursor.getPrivate('downPositionX');
      const endPosition = cursor.getPrivate('positionX');

      if (startPosition == null || endPosition == null) {
        return;
      }

      const selectionStart = Math.min(startPosition, endPosition);
      const selectionEnd = Math.max(startPosition, endPosition);

      if (Math.abs(selectionEnd - selectionStart) < 0.001) {
        return;
      }

      const dateDebut = xAxis.positionToDate(selectionStart);
      const dateFin   = xAxis.positionToDate(selectionEnd);
      xAxis.zoomToDates(dateDebut, dateFin, 0);
      options.onRangeSelect?.(dateDebut, dateFin);
      // La sélection amCharts masque ses tooltips natifs. Les réafficher sans
      // attendre un mouvement supplémentaire de la souris évite un état vide.
      synchroniserTooltips(endPosition);
    });

    cursor.events.on('cursormoved', () => {
      const positionX = cursor.getPrivate('positionX');

      if (positionX == null || !xAxis) {
        return;
      }

      planifierSynchronisation(positionX);
    });

    cursor.events.on('cursorhidden', () => {
      if (frameRequestId != null) {
        cancelAnimationFrame(frameRequestId);
        frameRequestId = null;
      }
      pendingPositionX = undefined;
      masquerTooltips();
    });
  }

  return cursor;
}
